import React, { useState } from "react";
import useSWR from "swr";
import { Table, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getIndicatorGroupList, createIndicatorGroup, updateIndicatorGroup,
  deleteIndicatorGroup, toggleIndicatorGroupUse,
} from "../api/domainApi";
import { IndicatorGroupRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;
const AXIS_OPTIONS = [{ value: "X", label: "X" }, { value: "Y", label: "Y" }, { value: "Z", label: "Z" }];

const IndicatorGroupPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IndicatorGroupRes | null>(null);
  const [form] = Form.useForm();

  const swrKey = `/indicator-group?page=${page}&kw=${keyword}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getIndicatorGroupList({ page: page - 1, size: PAGE_SIZE, keyword }),
    { keepPreviousData: true }
  );

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: IndicatorGroupRes) => {
    setEditing(row);
    form.setFieldsValue(row);
    setModalOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateIndicatorGroup(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createIndicatorGroup(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteIndicatorGroup(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<IndicatorGroupRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "코드", dataIndex: "grpCode", width: 100 },
    { title: "그룹명", dataIndex: "grpName", ellipsis: { showTitle: true } },
    { title: "축", dataIndex: "axisType", width: 60 },
    { title: "순서", dataIndex: "sortOrder", width: 60 },
    { title: "지표 수", dataIndex: "indicatorCount", width: 80 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 180,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" onClick={async () => { try { await toggleIndicatorGroupUse(row.id); mutate(); } catch (e: any) { message.error(e.message); } }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="지표그룹" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }}>
        <Input placeholder="그룹명 검색" value={kwInput} onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }} style={{ width: 220 }} />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={() => { setKwInput(""); setKeyword(""); setPage(1); }}>초기화</Button>
      </Space>
      <Table
        rowKey="id" columns={columns} dataSource={data?.content} loading={isLoading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: data?.page?.totalElements, onChange: setPage, showSizeChanger: false }}
        size="small"
      />
      <Modal
        title={editing ? "지표그룹 수정" : "지표그룹 추가"}
        open={modalOpen} onOk={handleOk} onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"} cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="grpCode" label="그룹 코드" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="grpName" label="그룹명" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="axisType" label="축 유형" rules={[{ required: true }]}>
            <Select options={AXIS_OPTIONS} />
          </Form.Item>
          <Form.Item name="sortOrder" label="정렬 순서" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default IndicatorGroupPage;
